import cardBackUrl from '@/assets/table/card-back.svg?url';
import type { Card } from '@/engine/types';

const SUIT_SYMBOL = { s: '♠', h: '♥', d: '♦', c: '♣' } as const;

export function suitSymbol(suit: string): string {
  return SUIT_SYMBOL[suit as keyof typeof SUIT_SYMBOL] ?? '?';
}

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<Size, { face: string; rank: string; suit: string }> = {
  sm: { face: 'w-7 h-10 rounded-sm', rank: 'text-sm', suit: 'text-xs' },
  md: { face: 'w-11 h-16 rounded-sm', rank: 'text-lg', suit: 'text-base' },
  lg: { face: 'w-14 h-20 rounded-md', rank: 'text-2xl', suit: 'text-xl' },
  xl: { face: 'w-16 h-24 rounded-md', rank: 'text-3xl', suit: 'text-2xl' },
};

export type CardFaceProps = {
  card?: Card;
  size?: Size;
  /** Label for `role="img"`. Defaults to `card` or "back"/"undealt". */
  backLabel?: 'back' | 'undealt';
};

/**
 * A single playing card. V2 treatment + V2.3 flip animation:
 *   - Face: cream paper with chunky pixel rank + suit symbol. Mounts
 *     with `.card-flip-in` (3D rotateY from -180° → 0°) — reads as the
 *     dealer snapping the card onto the felt.
 *   - Back: pixel SVG card-back. Mounts with `.card-back-in` (small
 *     scale-pop so face-down cards "appear" in hand gracefully).
 *   - Undealt: static dashed placeholder.
 *
 * Since callers render cards with `key={card ?? slotId}`, every time a
 * new card value lands in a slot React remounts the element, which
 * replays the CSS entrance animation naturally.
 */
export function CardFace({ card, size = 'md', backLabel = 'back' }: CardFaceProps) {
  const { face, rank: rankClass, suit: suitClass } = SIZE_CLASS[size];
  if (!card) {
    if (backLabel === 'undealt') {
      return (
        <div
          role="img"
          aria-label="undealt"
          className={`${face} border-2 border-dashed border-border bg-bg-deeper/40`}
        />
      );
    }
    return (
      <div
        role="img"
        aria-label="back"
        className={`${face} overflow-hidden pixelated card-back-in`}
      >
        <img src={cardBackUrl} alt="" draggable={false} className="w-full h-full pixelated" />
      </div>
    );
  }
  const rank = card[0];
  const suit = card[1];
  const red = suit === 'h' || suit === 'd';
  const accentBorder = red ? 'border-accent/70' : 'border-accent-alt/60';
  return (
    <div
      role="img"
      aria-label={card}
      className={`${face} border-2 ${accentBorder} bg-fg font-display flex flex-col items-center justify-center gap-0.5 ${red ? 'text-accent' : 'text-bg-deeper'} shadow-[0_2px_0_hsl(var(--color-bg-deeper)),0_0_12px_hsl(var(--color-bg-deeper)/0.7)] card-flip-in`}
    >
      <span className={`${rankClass} leading-none font-bold`}>{rank}</span>
      <span className={`${suitClass} leading-none`}>{suitSymbol(suit)}</span>
    </div>
  );
}
